import { Field, Formik, FormikProps } from 'formik';
import React from 'react';
import { ContactInfoFormFields } from '../../models/forms.model';
import { contactInfoFormSchema } from '../../validationSchemas/contactInfoFormSchema';
import { FormField } from '../FormFields/FormField';
import { PHONE_NUMBER_MASK } from '../Tabs/PersonalInfo';
import MaskedInput from 'react-text-mask';
import {
  normalizeContactPhoneNumber,
  saveContactInfo,
  validateContactEmail,
  validateContactPhoneNumber,
  ContactApiFailures,
} from './ContactInfoForm';

interface LoyaltyContactInfoFormProps {
  closeModal?: (closed: boolean) => void;
  currentUserInfo?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  };
  /** Checkout layout: same fields/validation/save, no confirm button (guest-style). */
  inline?: boolean;
  formRef?: React.Ref<FormikProps<ContactInfoFormFields>>;
  /** Saved customer email/phone — skip async validate when unchanged. */
  knownEmail?: string | null;
  knownPhone?: string | null;
}

/**
 * Loyalty contact form. Modal and inline checkout share the same initial values,
 * schema, email/phone validation, and saveContactInfo — only layout differs for inline.
 */
const LoyaltyContactInfoForm: React.FC<LoyaltyContactInfoFormProps> = ({
  closeModal,
  currentUserInfo,
  inline = false,
  formRef,
  knownEmail,
  knownPhone,
}) => {
  const initialFormState: ContactInfoFormFields = {
    first_name: currentUserInfo?.first_name ?? '',
    last_name: currentUserInfo?.last_name ?? '',
    email: currentUserInfo?.email ?? '',
    phone: normalizeContactPhoneNumber(currentUserInfo?.phone ?? ''),
  };

  const handleFormSubmission = async (
    values: ContactInfoFormFields,
    { setSubmitting, setFieldError }: any
  ) => {
    const [emailValidation, phoneValidation] = await Promise.all([
      values.email
        ? validateContactEmail(values.email, knownEmail)
        : Promise.resolve<{ valid: boolean; message?: string }>({ valid: true }),
      values.phone
        ? validateContactPhoneNumber(values.phone, knownPhone)
        : Promise.resolve<{ valid: boolean; message?: string }>({ valid: true }),
    ]);

    let hasError = false;
    if (!emailValidation.valid) {
      setFieldError('email', emailValidation.message);
      hasError = true;
    }
    if (!phoneValidation.valid) {
      setFieldError('phone', phoneValidation.message);
      hasError = true;
    }
    if (hasError) {
      setSubmitting(false);
      return;
    }

    saveContactInfo(values);
    closeModal?.(true);
  };

  const formClassName = inline
    ? 'new_form_design profile-info-read-only-field'
    : 'new_form_design w-75 my-0 mx-auto delivery_location_select full_width_mob full-width-ipad-modal';

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialFormState}
      enableReinitialize
      validationSchema={contactInfoFormSchema}
      onSubmit={handleFormSubmission}
    >
      {({
        values,
        errors,
        touched,
        status,
        setFieldValue,
        setFieldTouched,
        setStatus,
        validateField,
        handleSubmit,
        isSubmitting,
        submitForm,
      }) => (
        <form className={formClassName} onSubmit={handleSubmit}>
          <FormField
            labelText="First Name"
            placeholder="Enter your first name"
            type="text"
            name="first_name"
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('first_name', value, false).then(() => {
                validateField('first_name');
              });
            }}
            value={values.first_name}
            dataTestId="customer-first-name-input"
          />
          <FormField
            labelText="Last Name"
            placeholder="Enter your last name"
            type="text"
            name="last_name"
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('last_name', value, false).then(() => {
                validateField('last_name');
              });
            }}
            value={values.last_name}
            dataTestId="customer-last-name-input"
          />
          <FormField
            labelText="Email Address"
            placeholder="Enter your email address"
            type="email"
            name="email"
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              const apiFailures = (
                status as { apiFailures?: ContactApiFailures } | undefined
              )?.apiFailures;
              if (apiFailures?.email != null) {
                setStatus({
                  ...(status || {}),
                  apiFailures: { ...apiFailures, email: undefined },
                });
              }
              setFieldValue('email', value, false).then(() => {
                validateField('email');
              });
            }}
            value={values.email}
            dataTestId="customer-email-input"
          />
          <div>
            <label htmlFor="phone">Phone Number</label>
            <Field
              name="phone"
              render={({ field }) => (
                <MaskedInput
                  {...field}
                  mask={PHONE_NUMBER_MASK}
                  id="phone"
                  placeholder="(223) 123-1234"
                  type="text"
                  value={values.phone}
                  onBlur={() => {
                    setFieldTouched('phone', true, false);
                    validateField('phone');
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[() -]/g, '');
                    const apiFailures = (
                      status as { apiFailures?: ContactApiFailures } | undefined
                    )?.apiFailures;
                    if (apiFailures?.phone != null) {
                      setStatus({
                        ...(status || {}),
                        apiFailures: { ...apiFailures, phone: undefined },
                      });
                    }
                    setFieldValue('phone', value, false).then(() => {
                      validateField('phone');
                    });
                  }}
                  guide={true}
                  className={`
                          ${
                            errors.phone && touched.phone
                              ? 'input_error'
                              : 'text-input'
                          }
                            form-control`}
                />
              )}
            />
            {errors.phone && touched.phone && (
              <div className="input-feedback clr-dark-red f-s14 font-rale mt-1 d-inline-block">
                {errors.phone}
              </div>
            )}
          </div>
          {!inline && (
            <div className="d-flex justify-content-center">
              <button
                type="submit"
                className="btn btn-large py-2 f-s16 mb-4 mt-3 text-uppercase"
                disabled={isSubmitting}
                onClick={submitForm}
                data-testid="update-contact-info-on-checkout"
              >
                CONFIRM CONTACT INFO
              </button>
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};

export default LoyaltyContactInfoForm;
